import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin, ChefHat } from 'lucide-react';
import { getTowns, getOffers, getFacets } from '../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DealCard from '../components/DealCard';
import DaySelector from '../components/DaySelector';

const DealsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [towns, setTowns] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTown, setSelectedTown] = useState(searchParams.get('town') || '');
  const [selectedDay, setSelectedDay] = useState(searchParams.get('day') || null);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedGroup, setSelectedGroup] = useState(searchParams.get('group') || 'all');
  const [cuisineGroups, setCuisineGroups] = useState([]);
  const [visibleCount, setVisibleCount] = useState(24);

  const CATEGORY_OPTIONS = [
    { value: 'all', label: 'All types' },
    { value: 'restaurant', label: 'Restaurants' },
    { value: 'cafe', label: 'Cafés' },
    { value: 'takeaway', label: 'Takeaways' },
    { value: 'pub', label: 'Pubs & Bars' },
  ];

  useEffect(() => {
    const fetchTowns = async () => {
      try {
        const res = await getTowns();
        setTowns(res.data);
      } catch (error) {
        console.error('Error fetching towns:', error);
      }
    };
    fetchTowns();
    getFacets().then((r) => setCuisineGroups(r.data.cuisine_groups || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchOffers = async () => {
      setLoading(true);
      try {
        const res = await getOffers(selectedTown || null, selectedDay, true, selectedCategory, selectedGroup);
        setOffers(res.data);
        setVisibleCount(24);
      } catch (error) {
        console.error('Error fetching offers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, [selectedTown, selectedDay, selectedCategory, selectedGroup]);

  const updateParam = (key, value, clearValue) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== clearValue) params.set(key, value);
    else params.delete(key);
    setSearchParams(params);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    updateParam('category', value, 'all');
  };

  const handleGroupChange = (value) => {
    setSelectedGroup(value);
    updateParam('group', value, 'all');
  };


  const handleTownChange = (value) => {
    const town = value === 'all' ? '' : value;
    setSelectedTown(town);
    const params = new URLSearchParams(searchParams);
    if (town) {
      params.set('town', town);
    } else {
      params.delete('town');
    }
    setSearchParams(params);
  };

  const handleDayChange = (day) => {
    setSelectedDay(day);
    const params = new URLSearchParams(searchParams);
    if (day) {
      params.set('day', day);
    } else {
      params.delete('day');
    }
    setSearchParams(params);
  };

  const selectedTownData = towns.find(t => t.id === selectedTown);
  const selectedTownName = selectedTownData?.name;

  // Generate dynamic page title
  const getPageTitle = () => {
    if (selectedTownName) {
      return `Browse ${selectedTownName}'s Deals`;
    }
    return 'Browse Deals';
  };

  return (
    <div className="min-h-screen bg-white" data-testid="deals-page">
      {/* Header */}
      <section className="bg-[#7B2CBF] py-10">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white text-center mb-2">
            {getPageTitle()}
          </h1>
          <p className="text-white/80 text-center max-w-xl mx-auto">
            {selectedTownName 
              ? `Discover the best daily specials from restaurants in ${selectedTownName}`
              : 'Find daily specials from restaurants in your area'
            }
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-[#E8E8E8] bg-[#F5F5F5]" data-testid="deals-filters">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-6">
          {/* Town / Category / Cuisine Filters */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#7B2CBF]" />
              <Select value={selectedTown || 'all'} onValueChange={handleTownChange}>
                <SelectTrigger className="w-[180px] bg-white" data-testid="deals-town-select">
                  <SelectValue placeholder="All Towns" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Towns</SelectItem>
                  {towns.map((town) => (
                    <SelectItem key={town.id} value={town.id}>
                      {town.name}{town.region && `, ${town.region}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-[160px] bg-white" data-testid="deals-category-select">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedGroup} onValueChange={handleGroupChange}>
              <SelectTrigger className="w-[170px] bg-white" data-testid="deals-cuisine-select">
                <SelectValue placeholder="All cuisines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cuisines</SelectItem>
                {cuisineGroups.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Day Selector */}
          <DaySelector 
            selectedDay={selectedDay} 
            onSelect={handleDayChange} 
          />
        </div>
      </section>

      {/* Results */}
      <section className="py-8 sm:py-12">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          {/* Results Header */}
          <div className="mb-6">
            <h2 className="text-xl font-bold text-[#242E30]">
              {selectedTownName ? `Deals in ${selectedTownName}` : 'All Deals'}
              {selectedDay && ` for ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}`}
            </h2>
            <p className="text-[#595959] text-sm mt-1">
              {offers.length} {offers.length === 1 ? 'result' : 'results'} found
            </p>
          </div>

          {/* Deals Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="bg-white border border-[#E8E8E8] rounded-xl overflow-hidden animate-pulse">
                  <div className="w-full h-40 bg-[#F5F5F5]" />
                  <div className="p-4">
                    <div className="h-5 bg-[#F5F5F5] rounded w-3/4 mb-2" />
                    <div className="h-4 bg-[#F5F5F5] rounded w-1/2 mb-2" />
                    <div className="h-4 bg-[#F5F5F5] rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : offers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {offers.slice(0, visibleCount).map((offer, index) => (
                <DealCard key={offer.id || offer.restaurant_id} offer={offer} index={index % 24} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#F5F5F5] rounded-xl">
              <ChefHat className="h-16 w-16 text-[#D4D4D4] mx-auto mb-4" />
              <h3 className="text-lg font-bold text-[#242E30] mb-2">
                No Deals Found
              </h3>
              <p className="text-[#595959] max-w-md mx-auto">
                {selectedTown || selectedDay 
                  ? 'Try adjusting your filters to see more deals.'
                  : 'No deals are available at the moment. Check back soon!'}
              </p>
            </div>
          )}

          {!loading && offers.length > visibleCount && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => setVisibleCount((c) => c + 24)}
                className="bg-white border border-[#7B2CBF] text-[#7B2CBF] hover:bg-[#7B2CBF] hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors"
                data-testid="load-more-btn"
              >
                Load more ({offers.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DealsPage;
